import type * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Inbox,
  RefreshCw,
  ExternalLink,
  Check,
  Undo2,
  Calendar,
  ArrowDownToDot,
  ArrowUpFromLine,
  Lightbulb,
  Sparkles,
  Quote,
  Users,
} from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { TeachingBanner } from "../components/TeachingBanner";

/** Teams reconciliation surface — day-bucketed view that mirrors
 *  Mail's Daily Summary. Left rail is a tree of days (Today,
 *  Yesterday, recent weekdays, This Month, prior months). Right pane
 *  is the selected day's hits.
 *
 *  On mount we auto-scan Today if its persisted scan is missing or
 *  older than 1 hour. Every other day is user-initiated. State lives
 *  server-side per (user, day_iso) so navigating around / refreshing
 *  doesn't lose anything. */

interface DayApi {
  id: string;
  day_iso: string;
  status: "idle" | "running" | "done" | "cancelled" | "error" | "interrupted";
  scanned_at: string | null;
  total_messages: number;
  prefiltered: number;
  ai_calls: number;
  hit_count: number;
  total_ms: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  ai_provider: string | null;
  ai_model: string | null;
  error_msg: string | null;
}

/** Visual state derived from (day, scanning, hitsLoading, hits).
 *  Every render path through DayDetail dispatches off one of these
 *  six tokens — no more "no body state matches → blank pane". */
type DayKind =
  | "loading" //  hitsLoading=true, fetch in flight
  | "scanning_now" // active SSE stream for this day
  | "idle" //  no scan row exists yet
  | "done_with_hits" // scan complete + hits to show
  | "done_no_hits" // scan complete, zero follow-ups
  | "interrupted" // status=running but no live SSE; daemon crashed mid-scan
  | "cancelled" //  user cancelled
  | "error"; //  scan failed

function classifyDay(
  day: DayApi | null,
  scanning: boolean,
  hitsLoading: boolean,
  hits: HitApi[],
): DayKind {
  if (scanning) return "scanning_now";
  if (hitsLoading && hits.length === 0) return "loading";
  if (!day || day.status === "idle") return "idle";
  if (day.status === "running") return "interrupted";
  if (day.status === "cancelled") return "cancelled";
  if (day.status === "error") return "error";
  // status === "done"
  return hits.length > 0 ? "done_with_hits" : "done_no_hits";
}

interface AiVerdict {
  needs_followup: boolean;
  closure_observed: boolean;
  kind: "asked_of_waqas" | "waqas_promised" | "neither";
  confidence: number;
  reason: string;
  suggested_action: string;
}

interface Participant {
  mri: string;
  oid: string;
  name: string;
}

interface HitApi {
  id: string;
  message_id: string;
  conversation_id: string;
  thread_topic: string | null;
  thread_type: string | null;
  from_mri: string;
  from_name: string | null;
  from_oid: string | null;
  tenant_id: string | null;
  arrival_iso: string | null;
  snippet: string | null;
  is_sent_by_me: boolean;
  verdict: AiVerdict | null;
  chat_label: string | null;
  participants: Participant[];
  dismissed: boolean;
  dismissed_at: string | null;
  created_at: string;
}

const STALE_MS = 60 * 60 * 1000; // re-scan today if last scan > 1h ago

export function Teams() {
  const today = useMemo(() => localIsoDay(new Date()), []);
  const [days, setDays] = useState<Map<string, DayApi>>(new Map());
  const [loadedRanges, setLoadedRanges] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string>(today);
  const [hits, setHits] = useState<HitApi[]>([]);
  const [hitsLoading, setHitsLoading] = useState(false);
  const [scanning, setScanning] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState<{
    scanned: number;
    total: number;
    prefiltered: number;
    hits: number;
  } | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const autoTriggeredRef = useRef(false);
  const sseAbortRef = useRef<AbortController | null>(null);
  // Latest user-selected day. Every async handler (fetch hits, SSE
  // event:hit, post-scan reload) reads through THIS ref so a
  // response that arrives after the user has navigated away gets
  // dropped instead of clobbering the new day's state. Without it
  // we get the "old hits flash, then blank" symptom.
  const selectedRef = useRef(selected);
  // Loading-token: every fetch records the day it was issued for.
  // The success branch only writes state if the token still matches
  // selectedRef.current. Protects against stale-fetch races when
  // the user clicks two days quickly.
  const hitsRequestRef = useRef(0);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  /** Load metadata for ONE date range and merge into the days Map.
   *  Lazy per-month so the tree never fetches days the user can't
   *  see. Idempotent — same range key short-circuits the second
   *  call. Range key = `<from>:<to>` so monthStart-monthEnd dedups. */
  const loadDays = useCallback(
    async (from: string, to: string, force = false) => {
      const key = `${from}:${to}`;
      if (!force && loadedRanges.has(key)) return;
      try {
        const r = await fetch(
          `/api/actions/teams-scan-days?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
          { credentials: "same-origin" },
        );
        if (!r.ok) return;
        const j = (await r.json()) as { ok: boolean; days: DayApi[] };
        if (!j.ok) return;
        setDays((prev) => {
          const next = new Map(prev);
          for (const d of j.days) next.set(d.day_iso, d);
          return next;
        });
        setLoadedRanges((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      } catch (err) {
        // best-effort; swallow so a flaky call doesn't kill the page.
        console.warn("[teams] loadDays failed", err);
      }
    },
    [loadedRanges],
  );

  /** Load the selected day's hit list. Clears the panel
   *  IMMEDIATELY so we never flash the previous day's hits while
   *  the new fetch is in flight. The response is dropped if the
   *  user has navigated to a different day before it returns. */
  const loadHits = useCallback(async (dayIso: string) => {
    const token = ++hitsRequestRef.current;
    setHits([]); // clear stale day-A data BEFORE the fetch starts
    setHitsLoading(true);
    setWarnings([]);
    setScanProgress(null);
    try {
      const r = await fetch(`/api/actions/teams-scan-days/${dayIso}`, {
        credentials: "same-origin",
      });
      if (token !== hitsRequestRef.current || dayIso !== selectedRef.current) {
        return; // user moved on; drop this response
      }
      if (!r.ok) {
        setHits([]);
        return;
      }
      const j = (await r.json()) as { ok: boolean; day: DayApi | null; hits: HitApi[] };
      if (token !== hitsRequestRef.current || dayIso !== selectedRef.current) return;
      setHits(j.hits || []);
      if (j.day) {
        setDays((prev) => {
          const next = new Map(prev);
          next.set(dayIso, j.day!);
          return next;
        });
      }
    } catch (err) {
      if (token === hitsRequestRef.current) {
        setErrorBanner(`Failed to load ${dayIso}: ${(err as Error).message || err}`);
      }
    } finally {
      if (token === hitsRequestRef.current) setHitsLoading(false);
    }
  }, []);

  // First-load window: Today + 7 recent weekdays + the visible
  // "This month" group. Other months load when the user expands
  // their accordion.
  useEffect(() => {
    const todayDate = new Date(today + "T00:00:00");
    const monthStart = localIsoDay(
      new Date(todayDate.getFullYear(), todayDate.getMonth(), 1),
    );
    loadDays(monthStart, today);
  }, [today, loadDays]);

  // On day-switch: abort any in-flight scan that was for a DIFFERENT
  // day so its `event: hit` frames can't pollute the new day's
  // panel; then fetch the new day's hits.
  useEffect(() => {
    if (scanning && scanning !== selected) {
      sseAbortRef.current?.abort();
    }
    loadHits(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, loadHits]);

  /** Fire (or replace an in-flight) scan for a day. */
  const startScan = useCallback(
    async (dayIso: string) => {
      // Abort any prior scan (whether for this day or a different
      // one). The old SSE stream's handlers won't run after abort.
      sseAbortRef.current?.abort();
      const ctrl = new AbortController();
      sseAbortRef.current = ctrl;
      setScanning(dayIso);
      setScanProgress(null);
      setWarnings([]);
      setErrorBanner(null);
      // Don't pre-clear hits on re-scan. The server's UPSERT path
      // (dedup-merge by message_id) re-emits each surviving hit on
      // event:hit; our local handler updates by id so dismissed
      // entries persist and re-classified entries get fresh
      // verdicts in place. Pre-clearing made the panel "go blank"
      // for the duration of the scan.
      const csrf = getCsrfToken();
      try {
        const r = await fetch("/api/actions/scan-teams-day", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify({ day_iso: dayIso }),
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) {
          const txt = await r.text().catch(() => "");
          setErrorBanner(`Scan request failed (${r.status}). ${txt.slice(0, 200)}`);
          setScanning(null);
          return;
        }
        await consumeSse(r.body, {
          onEvent: (event, data) => {
            // Drop everything if the controller has been replaced
            // (a newer scan or a day-switch fired). Reading scanning
            // off React state is stale here; sseAbortRef is the
            // single source of truth.
            if (sseAbortRef.current !== ctrl) return;
            if (event === "preflight" || event === "progress") {
              const p = data as {
                scanned?: number;
                total?: number;
                prefiltered?: number;
                hits?: number;
                survivors?: number;
                total_messages?: number;
              };
              if (dayIso === selectedRef.current) {
                setScanProgress({
                  scanned: p.scanned ?? 0,
                  total: p.total ?? p.survivors ?? 0,
                  prefiltered: p.prefiltered ?? 0,
                  hits: p.hits ?? 0,
                });
              }
              if (event === "preflight" && (p.total_messages || p.survivors)) {
                setDays((prev) => {
                  const next = new Map(prev);
                  const cur = next.get(dayIso);
                  if (cur) {
                    next.set(dayIso, {
                      ...cur,
                      status: "running",
                      total_messages: p.total_messages ?? cur.total_messages,
                      prefiltered: p.prefiltered ?? cur.prefiltered,
                    });
                  }
                  return next;
                });
              }
            } else if (event === "hit") {
              const hit = data as HitApi;
              // Always update the tree-side badge so the day's
              // hit_count grows live.
              setDays((prev) => {
                const next = new Map(prev);
                const cur = next.get(dayIso);
                if (cur) next.set(dayIso, { ...cur, hit_count: cur.hit_count + 1 });
                return next;
              });
              // Only paint into the right pane when the user is
              // looking at THIS day. Without this gate, scrolling
              // away mid-scan would still mutate the right pane.
              if (dayIso === selectedRef.current) {
                setHits((prev) => {
                  const idx = prev.findIndex((h) => h.id === hit.id);
                  if (idx >= 0) {
                    const next = prev.slice();
                    next[idx] = hit;
                    return next;
                  }
                  return [...prev, hit];
                });
              }
            } else if (event === "warn") {
              const msg = (data as { message?: string }).message;
              if (msg && dayIso === selectedRef.current) {
                setWarnings((w) => [...w.slice(-4), msg]);
              }
            } else if (event === "done") {
              const d = data as DayApi;
              setDays((prev) => {
                const next = new Map(prev);
                const cur = next.get(dayIso);
                next.set(dayIso, { ...(cur ?? ({} as DayApi)), ...d, status: "done" });
                return next;
              });
            } else if (event === "error") {
              const msg =
                (data as { message?: string }).message || "Scan failed.";
              if (dayIso === selectedRef.current) setErrorBanner(msg);
            }
          },
        });
      } catch (e) {
        if ((e as Error).name !== "AbortError" && dayIso === selectedRef.current) {
          setErrorBanner(String((e as Error).message || e));
        }
      } finally {
        if (sseAbortRef.current === ctrl) {
          sseAbortRef.current = null;
          setScanning(null);
          setScanProgress(null);
          // Refresh just this day's metadata; the response from the
          // /:day_iso endpoint also brings any rows that landed
          // after the SSE final flush.
          loadDays(dayIso, dayIso, true);
          if (dayIso === selectedRef.current) loadHits(dayIso);
        }
      }
    },
    [loadDays, loadHits],
  );

  /** Auto-scan Today on first mount when the persisted scan is
   *  missing or older than STALE_MS. Don't auto-scan on every nav
   *  back to /teams. */
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (days.size === 0) return; // wait for first load
    const todayRow = days.get(today);
    const stale =
      !todayRow ||
      todayRow.status === "idle" ||
      todayRow.status === "error" ||
      (todayRow.scanned_at && Date.now() - Date.parse(todayRow.scanned_at) > STALE_MS);
    if (stale) {
      autoTriggeredRef.current = true;
      startScan(today);
    }
  }, [days, today, startScan]);

  return (
    <div className="flex h-[calc(100vh-1rem)]">
      <DayTree
        today={today}
        days={days}
        selected={selected}
        scanning={scanning}
        onSelect={setSelected}
        onMonthOpen={(monthIso) => {
          // monthIso is the first day of the requested month (e.g.
          // "2026-04-01"). Fetch (monthStart, monthEnd) lazily.
          const d = new Date(monthIso + "T00:00:00");
          const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
          loadDays(monthIso, localIsoDay(last));
        }}
      />
      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="mb-4">
          <TeachingBanner id="teams_overview" />
        </div>
        <DayDetail
          dayIso={selected}
          today={today}
          day={days.get(selected) ?? null}
          hits={hits}
          hitsLoading={hitsLoading}
          scanning={scanning === selected}
          scanProgress={scanning === selected ? scanProgress : null}
          warnings={scanning === selected ? warnings : []}
          errorBanner={errorBanner}
          onDismissError={() => setErrorBanner(null)}
          onScan={() => startScan(selected)}
          onCancel={() => {
            sseAbortRef.current?.abort();
            sseAbortRef.current = null;
            setScanning(null);
            setScanProgress(null);
          }}
          onDismiss={async (hitId) => {
            const csrf = getCsrfToken();
            await fetch(`/api/actions/teams-scan-day-hits/${hitId}/dismiss`, {
              method: "POST",
              credentials: "same-origin",
              headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
            });
            setHits((prev) =>
              prev.map((h) =>
                h.id === hitId ? { ...h, dismissed: true, dismissed_at: new Date().toISOString() } : h,
              ),
            );
            setDays((prev) => {
              const next = new Map(prev);
              const cur = next.get(selected);
              if (cur && cur.hit_count > 0) {
                next.set(selected, { ...cur, hit_count: cur.hit_count - 1 });
              }
              return next;
            });
          }}
          onUndismiss={async (hitId) => {
            const csrf = getCsrfToken();
            await fetch(`/api/actions/teams-scan-day-hits/${hitId}/undismiss`, {
              method: "POST",
              credentials: "same-origin",
              headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
            });
            setHits((prev) =>
              prev.map((h) =>
                h.id === hitId ? { ...h, dismissed: false, dismissed_at: null } : h,
              ),
            );
            setDays((prev) => {
              const next = new Map(prev);
              const cur = next.get(selected);
              if (cur) next.set(selected, { ...cur, hit_count: cur.hit_count + 1 });
              return next;
            });
          }}
        />
      </main>
    </div>
  );
}

interface DayTreeProps {
  today: string;
  days: Map<string, DayApi>;
  selected: string;
  scanning: string | null;
  onSelect: (day: string) => void;
  onMonthOpen: (monthFirstDayIso: string) => void;
}

function DayTree({ today, days, selected, scanning, onSelect, onMonthOpen }: DayTreeProps) {
  const groups = useMemo(() => buildDayGroups(today), [today]);
  const [openMonths, setOpenMonths] = useState<Set<string>>(() => {
    const init = new Set<string>();
    if (groups.thisMonth.length > 0) init.add("this-month");
    return init;
  });
  const toggleMonth = (id: string, firstDayIso?: string) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (firstDayIso) onMonthOpen(firstDayIso);
      }
      return next;
    });
  };

  return (
    <aside className="hidden md:flex w-[260px] flex-shrink-0 border-r border-slate-200/70 dark:border-slate-700/40 flex-col overflow-hidden">
      <div className="px-3 py-3 border-b border-slate-200/70 dark:border-slate-700/40">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Teams
        </div>
        <div className="text-base font-bold text-slate-900 dark:text-slate-100 mt-0.5">
          Daily reconciliation
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {groups.recent.map((d) => (
          <DayRow
            key={d.iso}
            label={d.label}
            iso={d.iso}
            day={days.get(d.iso) ?? null}
            selected={selected === d.iso}
            scanning={scanning === d.iso}
            onClick={() => onSelect(d.iso)}
          />
        ))}
        {groups.thisMonth.length > 0 && (
          <MonthGroup
            id="this-month"
            label="This month"
            entries={groups.thisMonth}
            days={days}
            selected={selected}
            scanning={scanning}
            open={openMonths.has("this-month")}
            onToggle={() => toggleMonth("this-month")}
            onSelect={onSelect}
          />
        )}
        {groups.priorMonths.map((m) => (
          <MonthGroup
            key={m.id}
            id={m.id}
            label={m.label}
            entries={m.entries}
            days={days}
            selected={selected}
            scanning={scanning}
            open={openMonths.has(m.id)}
            onToggle={() => toggleMonth(m.id, m.entries[m.entries.length - 1]?.iso)}
            onSelect={onSelect}
          />
        ))}
      </nav>
    </aside>
  );
}

function DayRow({
  label,
  iso,
  day,
  selected,
  scanning,
  onClick,
  indent = 0,
}: {
  label: string;
  iso: string;
  day: DayApi | null;
  selected: boolean;
  scanning: boolean;
  onClick: () => void;
  indent?: number;
}) {
  const padLeft = 8 + indent * 12;
  const hits = day?.hit_count ?? 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ paddingLeft: padLeft }}
      className={
        "group flex items-center gap-2 w-full pr-2 py-1.5 rounded-md text-left text-sm transition-colors " +
        (selected
          ? "bg-indigo-500 text-white shadow-sm"
          : "text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40")
      }
    >
      <span className="flex-1 truncate">{label}</span>
      {scanning ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
      ) : day == null ? (
        <span
          className={
            "text-[10px] font-bold flex-shrink-0 " +
            (selected ? "text-white/80" : "text-slate-400 dark:text-slate-500")
          }
          title="Not scanned yet"
        >
          ?
        </span>
      ) : day.status === "error" ? (
        <AlertCircle
          className={
            "w-3.5 h-3.5 flex-shrink-0 " + (selected ? "text-white" : "text-rose-500")
          }
        />
      ) : hits > 0 ? (
        <span
          className={
            "inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1 text-[11px] font-bold flex-shrink-0 " +
            (selected
              ? "bg-white/25 text-white"
              : "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300")
          }
        >
          {hits}
        </span>
      ) : (
        <CheckCircle2
          className={
            "w-3.5 h-3.5 flex-shrink-0 " + (selected ? "text-white/70" : "text-emerald-500")
          }
          aria-label="Scanned, no follow-ups"
        />
      )}
    </button>
  );
}

function MonthGroup({
  id,
  label,
  entries,
  days,
  selected,
  scanning,
  open,
  onToggle,
  onSelect,
}: {
  id: string;
  label: string;
  entries: { iso: string; label: string }[];
  days: Map<string, DayApi>;
  selected: string;
  scanning: string | null;
  open: boolean;
  onToggle: () => void;
  onSelect: (day: string) => void;
}) {
  const totalHits = entries.reduce((sum, e) => sum + (days.get(e.iso)?.hit_count ?? 0), 0);
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40 transition-colors"
        aria-expanded={open}
        aria-controls={`month-${id}`}
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        )}
        <span className="flex-1 truncate">{label}</span>
        {totalHits > 0 && (
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
            {totalHits}
          </span>
        )}
      </button>
      {open && (
        <div id={`month-${id}`} className="space-y-0.5">
          {entries.map((e) => (
            <DayRow
              key={e.iso}
              label={e.label}
              iso={e.iso}
              day={days.get(e.iso) ?? null}
              selected={selected === e.iso}
              scanning={scanning === e.iso}
              onClick={() => onSelect(e.iso)}
              indent={1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DayDetailProps {
  dayIso: string;
  today: string;
  day: DayApi | null;
  hits: HitApi[];
  hitsLoading: boolean;
  scanning: boolean;
  scanProgress: { scanned: number; total: number; prefiltered: number; hits: number } | null;
  warnings: string[];
  errorBanner: string | null;
  onDismissError: () => void;
  onScan: () => void;
  onCancel: () => void;
  onDismiss: (hitId: string) => void;
  onUndismiss: (hitId: string) => void;
}

type SortKey = "confidence" | "time" | "kind";

function DayDetail({
  dayIso,
  today,
  day,
  hits,
  hitsLoading,
  scanning,
  scanProgress,
  warnings,
  errorBanner,
  onDismissError,
  onScan,
  onCancel,
  onDismiss,
  onUndismiss,
}: DayDetailProps) {
  const [sortKey, setSortKey] = useState<SortKey>("confidence");
  const [showDismissed, setShowDismissed] = useState(false);
  const isToday = dayIso === today;
  const isYesterday = dayIso === isoOffset(today, -1);
  const headline = isToday
    ? "Today"
    : isYesterday
      ? "Yesterday"
      : new Date(dayIso + "T00:00:00").toLocaleDateString(undefined, {
          weekday: "long",
          month: "long",
          day: "numeric",
        });

  const [active, dismissed] = useMemo(() => {
    const a: HitApi[] = [];
    const d: HitApi[] = [];
    for (const h of hits) (h.dismissed ? d : a).push(h);
    const sortFn = (x: HitApi, y: HitApi) => {
      if (sortKey === "confidence") return (y.verdict?.confidence ?? 0) - (x.verdict?.confidence ?? 0);
      if (sortKey === "kind") {
        const ord = (h: HitApi) =>
          h.verdict?.kind === "asked_of_waqas" ? 0 : h.verdict?.kind === "waqas_promised" ? 1 : 2;
        return ord(x) - ord(y) || (y.verdict?.confidence ?? 0) - (x.verdict?.confidence ?? 0);
      }
      return Date.parse(y.arrival_iso || "") - Date.parse(x.arrival_iso || "");
    };
    return [a.sort(sortFn), d.sort(sortFn)];
  }, [hits, sortKey]);

  const kind: DayKind = useMemo(
    () => classifyDay(day, scanning, hitsLoading, hits),
    [day, scanning, hitsLoading, hits],
  );

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header className="space-y-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
          <Calendar className="w-3.5 h-3.5" />
          Teams · {dayIso}
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {headline}
        </h1>
        <DayHeaderMeta day={day} scanning={scanning} />
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {kind === "scanning_now" ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5"
          >
            Cancel scan
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              // Belt-and-suspenders: explicit preventDefault so a
              // future stray <form> ancestor never triggers a page
              // submit / refresh on click.
              e.preventDefault();
              e.stopPropagation();
              onScan();
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5"
            disabled={kind === "loading"}
          >
            {scanLabel(kind)}
          </button>
        )}
        {dismissed.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDismissed((v) => !v)}
            className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:underline"
          >
            {showDismissed ? "Hide" : "Show"} {dismissed.length} handled
          </button>
        )}
      </div>

      {errorBanner && (
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{errorBanner}</span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismissError();
            }}
            className="text-rose-700 dark:text-rose-200 hover:underline font-semibold"
          >
            Dismiss
          </button>
        </div>
      )}

      {scanning && scanProgress && (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>
              Scanning {scanProgress.scanned} of {scanProgress.total} · {scanProgress.prefiltered} pre-filtered ·{" "}
              {scanProgress.hits} hit{scanProgress.hits === 1 ? "" : "s"} so far
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-[width] duration-200"
              style={{
                width: `${
                  scanProgress.total > 0
                    ? Math.min(100, Math.round((scanProgress.scanned / scanProgress.total) * 100))
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <details className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          <summary className="cursor-pointer font-semibold">
            {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}

      {(active.length > 0 || (showDismissed && dismissed.length > 0)) && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 dark:text-slate-400">Sort:</span>
          {(["confidence", "kind", "time"] as SortKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setSortKey(k)}
              className={
                "rounded-full px-3 py-1 font-semibold capitalize " +
                (sortKey === k
                  ? "bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200")
              }
            >
              {k}
            </button>
          ))}
        </div>
      )}

      {/* Empty / status states are driven by classifyDay() so every
          render path lands on one explicit state token. No more
          "no body state matches → blank pane". */}
      {kind === "loading" && (
        <EmptyState title="Loading…" body="" loader />
      )}
      {kind === "idle" && (
        <EmptyState
          title={isToday ? "Today's scan starts on tab open" : "Not scanned yet"}
          body={
            isToday
              ? "Auto-scanning your Teams chat for the last 24 hours, hits will appear here as they're found."
              : "Click \"Scan this day\" above to look for messages where you were asked to do something or promised to send something, with no closure observed yet."
          }
          loader={isToday}
        />
      )}
      {kind === "done_no_hits" && active.length === 0 && (
        <EmptyState
          title="No follow-ups for this day."
          body="Every Teams message in this window either got resolved, did not need a response, or has already been marked handled."
          icon="ok"
        />
      )}
      {kind === "interrupted" && active.length === 0 && (
        <EmptyState
          title="Scan was interrupted."
          body={
            day?.error_msg ||
            "The previous scan didn't finish. Hits found before the interruption may already be persisted; click Resume scan to pick up where it left off."
          }
          icon="error"
        />
      )}
      {kind === "cancelled" && active.length === 0 && (
        <EmptyState
          title="Scan was cancelled."
          body="No follow-ups were captured. Click Re-scan to start over."
          icon="error"
        />
      )}
      {kind === "error" && active.length === 0 && (
        <EmptyState
          title="Scan failed."
          body={day?.error_msg || "The daemon couldn't complete this day's scan. Click Retry above."}
          icon="error"
        />
      )}

      {active.length > 0 && (
        <ul className="space-y-2">
          {active.map((h) => (
            <HitCard key={h.id} hit={h} onDismiss={() => onDismiss(h.id)} />
          ))}
        </ul>
      )}
      {showDismissed && dismissed.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 pt-2">
            Handled
          </div>
          <ul className="space-y-2 opacity-60">
            {dismissed.map((h) => (
              <HitCard
                key={h.id}
                hit={h}
                onUndismiss={() => onUndismiss(h.id)}
                isDismissed
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function DayHeaderMeta({ day, scanning }: { day: DayApi | null; scanning: boolean }) {
  if (!day || day.status === "idle") {
    return (
      <div className="text-sm text-slate-500 dark:text-slate-400">
        {scanning ? "Scanning..." : "Not scanned yet."}
      </div>
    );
  }
  const parts: string[] = [];
  if (day.scanned_at) {
    parts.push(`Scanned ${formatRelativeShort(day.scanned_at)}`);
  }
  if (day.ai_model) parts.push(`via ${day.ai_model}`);
  if (day.cost_usd > 0) parts.push(`$${day.cost_usd.toFixed(4)}`);
  if (day.total_messages) parts.push(`${day.total_messages} msgs · ${day.ai_calls} AI calls`);
  return (
    <div className="text-sm text-slate-500 dark:text-slate-400">{parts.join(" · ")}</div>
  );
}

function HitCard({
  hit,
  onDismiss,
  onUndismiss,
  isDismissed = false,
}: {
  hit: HitApi;
  onDismiss?: () => void;
  onUndismiss?: () => void;
  isDismissed?: boolean;
}) {
  const v = hit.verdict;
  const tier = confidenceTier(v?.confidence ?? 0);
  const deepLink = buildTeamsDeepLink(hit);
  const chatLabel = hit.chat_label || friendlyChatFallback(hit);
  return (
    <li className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur overflow-hidden">
      {/* HEADER STRIP */}
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 pt-3 pb-2.5 border-b border-slate-200/60 dark:border-slate-700/40">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span
              className={
                "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full " +
                tier.classes
              }
            >
              {tier.label}
            </span>
            <KindBadge kind={v?.kind ?? "neither"} />
            {hit.thread_type && (
              <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                {hit.thread_type}
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 break-words leading-snug">
            {chatLabel}
          </div>
          {hit.arrival_iso && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              {formatDateTime(hit.arrival_iso)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {deepLink && (
            <a
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-2.5 py-1.5"
              title="Open in Teams (native app via msteams:// URL scheme)"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in Teams
            </a>
          )}
          {!isDismissed && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5"
              title="Mark as handled, future scans skip this message"
            >
              <Check className="w-3.5 h-3.5" />
              Handled
            </button>
          )}
          {isDismissed && onUndismiss && (
            <button
              type="button"
              onClick={onUndismiss}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5"
            >
              <Undo2 className="w-3.5 h-3.5" />
              Restore
            </button>
          )}
        </div>
      </div>

      {/* PARTICIPANTS */}
      {hit.participants && hit.participants.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400 border-b border-slate-200/60 dark:border-slate-700/40">
          <Users className="w-3 h-3 flex-shrink-0" />
          <span className="font-semibold uppercase tracking-wider">Participants</span>
          <ParticipantChips participants={hit.participants} />
        </div>
      )}

      {/* QUOTED MESSAGE */}
      {hit.snippet && (
        <div className="px-4 py-3 border-b border-slate-200/60 dark:border-slate-700/40">
          <div className="flex items-start gap-2">
            <Quote className="w-3.5 h-3.5 mt-0.5 text-sky-500 flex-shrink-0" />
            <ExpandableQuote
              text={hit.snippet}
              author={
                hit.is_sent_by_me
                  ? "You"
                  : hit.from_name || hit.participants.find((p) => p.mri === hit.from_mri)?.name || "Sender"
              }
            />
          </div>
        </div>
      )}

      {/* AI REASONING */}
      {v?.reason && (
        <div className="px-4 py-3 border-b border-slate-200/60 dark:border-slate-700/40">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-violet-500" />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
              AI reasoning
            </span>
          </div>
          <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed pl-4.5">
            {v.reason}
          </div>
        </div>
      )}

      {/* SUGGESTED ACTION */}
      {v?.suggested_action && (
        <div className="px-4 py-3 bg-amber-50/40 dark:bg-amber-950/15">
          <div className="flex items-center gap-1.5 mb-1">
            <Lightbulb className="w-3 h-3 text-amber-500" />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300">
              Suggestion
            </span>
          </div>
          <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed pl-4.5">
            {v.suggested_action}
          </div>
        </div>
      )}
    </li>
  );
}

function ParticipantChips({ participants }: { participants: Participant[] }) {
  const ordered = useMemo(() => {
    const me = participants.find((p) => p.name === "You");
    const others = participants.filter((p) => p.name !== "You");
    return me ? [me, ...others] : participants;
  }, [participants]);
  const visible = ordered.slice(0, 4);
  const overflow = ordered.length - visible.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((p, i) => (
        <span
          key={p.mri || i}
          className={
            "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold " +
            (p.name === "You"
              ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200"
              : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200")
          }
          title={p.mri}
        >
          <span
            className="w-3.5 h-3.5 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 dark:from-slate-500 dark:to-slate-700 text-white text-[8px] font-bold inline-flex items-center justify-center"
            aria-hidden
          >
            {(p.name || "?").trim().charAt(0).toUpperCase()}
          </span>
          <span className="truncate max-w-[120px]">{p.name}</span>
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
          +{overflow} more
        </span>
      )}
    </div>
  );
}

function ExpandableQuote({ text, author }: { text: string; author: string }) {
  const [expanded, setExpanded] = useState(false);
  const looksLong = text.length > 220;
  return (
    <blockquote className="min-w-0 flex-1 border-l-[3px] border-sky-400 dark:border-sky-500 pl-3 italic text-sm text-slate-700 dark:text-slate-200">
      <div className="text-[10px] not-italic uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 mb-0.5">
        {author}
      </div>
      <div className={expanded ? "" : "line-clamp-4"}>"{text}"</div>
      {looksLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] not-italic font-semibold text-sky-600 dark:text-sky-400 hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </blockquote>
  );
}

function scanLabel(kind: DayKind): React.ReactNode {
  switch (kind) {
    case "scanning_now":
      return "Scanning…";
    case "loading":
      return "Loading…";
    case "done_with_hits":
    case "done_no_hits":
      return (
        <>
          <RefreshCw className="w-3.5 h-3.5" />
          Re-scan this day
        </>
      );
    case "error":
      return (
        <>
          <RefreshCw className="w-3.5 h-3.5" />
          Retry scan
        </>
      );
    case "cancelled":
      return (
        <>
          <RefreshCw className="w-3.5 h-3.5" />
          Re-run scan
        </>
      );
    case "interrupted":
      return (
        <>
          <RefreshCw className="w-3.5 h-3.5" />
          Resume scan
        </>
      );
    case "idle":
    default:
      return <>Scan this day</>;
  }
}

function friendlyChatFallback(hit: HitApi): string {
  // Server resolves chat_label at scan time; this only fires for
  // older rows scanned before migration 0042 landed.
  if (hit.thread_topic) return hit.thread_topic;
  if (hit.from_name) return `Direct chat with ${hit.from_name}`;
  const conv = hit.conversation_id || "";
  // Strip the GUID-soup down to a short prefix so the card never
  // renders the full MRI.
  return conv.split("@")[0].slice(0, 24) + (conv.length > 24 ? "..." : "");
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function KindBadge({ kind }: { kind: AiVerdict["kind"] }) {
  if (kind === "asked_of_waqas") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200">
        <ArrowDownToDot className="w-3 h-3" />
        asked of you
      </span>
    );
  }
  if (kind === "waqas_promised") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
        <ArrowUpFromLine className="w-3 h-3" />
        you promised
      </span>
    );
  }
  return null;
}

function EmptyState({
  title,
  body,
  loader,
  icon,
}: {
  title: string;
  body: string;
  loader?: boolean;
  icon?: "ok" | "error";
}) {
  return (
    <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 text-center">
      {loader ? (
        <Loader2 className="w-8 h-8 mx-auto text-indigo-500 animate-spin" strokeWidth={2.5} />
      ) : icon === "ok" ? (
        <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500" strokeWidth={2.5} />
      ) : icon === "error" ? (
        <AlertCircle className="w-8 h-8 mx-auto text-rose-500" strokeWidth={2.5} />
      ) : (
        <Inbox className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-500" strokeWidth={2.5} />
      )}
      <h3 className="mt-3 text-lg font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">{body}</p>
    </div>
  );
}

/** Build the canonical Teams deep-link for a hit. The msteams://
 *  URL scheme launches the native macOS / Windows Teams app and
 *  navigates to the conversation. We pick the right shape based on
 *  the conversation id format the cache exposes:
 *    - 1:1 chats          : 19:<oidA>_<oidB>@unq.gbl.spaces        -> l/chat/0/0?users=<otherOid>
 *    - group chats / channels (modern)         : 19:<id>@thread.tacv2 -> l/message/<convId>/<msgId>
 *    - meeting chats       : 19:meeting_<base64id>@thread.v2          -> l/message/<convId>/<msgId>
 *    - skype legacy        : 019:<hex>@thread.skype                  -> l/message/<convId>/<msgId>
 *  Returns null if we don't have enough metadata. */
function buildTeamsDeepLink(hit: HitApi): string | null {
  const conv = hit.conversation_id || "";
  if (!conv) return null;
  // 1:1 chat: extract the OTHER user's oid. Both endpoints look like
  // 19:<oidA>_<oidB>@unq.gbl.spaces — we use the sender's oid (which
  // is by definition the other party from Waqas's perspective) when
  // is_sent_by_me=false; otherwise we'd need Waqas's own oid which
  // we can recover from the trailing @unq.gbl.spaces convention.
  const oneToOne = conv.match(/^19:([0-9a-f-]+)_([0-9a-f-]+)@unq\.gbl\.spaces$/i);
  if (oneToOne) {
    const other =
      hit.from_oid && !hit.is_sent_by_me
        ? hit.from_oid
        : oneToOne[1] === (hit.from_oid || "")
          ? oneToOne[2]
          : oneToOne[1];
    if (other) return `msteams://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(other)}`;
  }
  // Channel / group / meeting / skype-legacy: l/message/<convId>/<msgId>
  // Tenant id ALSO required for channel deep-links to land cleanly.
  if (hit.message_id) {
    const params = new URLSearchParams();
    if (hit.tenant_id) params.set("tenantId", hit.tenant_id);
    params.set("context", '{"contextType":"chat"}');
    return `msteams://teams.microsoft.com/l/message/${encodeURIComponent(conv)}/${encodeURIComponent(hit.message_id)}?${params.toString()}`;
  }
  return null;
}

function confidenceTier(c: number): { label: string; classes: string } {
  if (c >= 0.75)
    return {
      label: "high confidence",
      classes: "bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200",
    };
  if (c >= 0.5)
    return {
      label: "medium",
      classes: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200",
    };
  return {
    label: "low",
    classes: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200",
  };
}

function buildDayGroups(today: string) {
  const recent: { iso: string; label: string }[] = [];
  const todayDate = new Date(today + "T00:00:00");
  const yesterdayIso = isoOffset(today, -1);
  recent.push({ iso: today, label: "Today" });
  recent.push({ iso: yesterdayIso, label: "Yesterday" });
  // Days 2-6 ago: weekday + date label.
  for (let i = 2; i <= 6; i++) {
    const iso = isoOffset(today, -i);
    const d = new Date(iso + "T00:00:00");
    recent.push({
      iso,
      label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    });
  }

  // This month: any day in the current month older than 6 days back
  // and on or after the 1st.
  const thisMonth: { iso: string; label: string }[] = [];
  const monthStartIso = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const minRecent = isoOffset(today, -6);
  let cursor = isoOffset(minRecent, -1);
  while (cursor >= monthStartIso) {
    const d = new Date(cursor + "T00:00:00");
    thisMonth.push({
      iso: cursor,
      label: d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" }),
    });
    cursor = isoOffset(cursor, -1);
  }

  // Prior 6 months. Each month is a collapsible group with day-of-
  // month entries 1..lastOfMonth (descending).
  const priorMonths: {
    id: string;
    label: string;
    entries: { iso: string; label: string }[];
  }[] = [];
  for (let mback = 1; mback <= 6; mback++) {
    const ref = new Date(todayDate.getFullYear(), todayDate.getMonth() - mback, 1);
    const monthLabel = ref.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    const lastDay = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
    const entries: { iso: string; label: string }[] = [];
    for (let day = lastDay; day >= 1; day--) {
      const dt = new Date(ref.getFullYear(), ref.getMonth(), day);
      const iso = localIsoDay(dt);
      entries.push({
        iso,
        label: dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric" }),
      });
    }
    priorMonths.push({
      id: `m-${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, "0")}`,
      label: monthLabel,
      entries,
    });
  }

  return { recent, thisMonth, priorMonths };
}

function localIsoDay(d: Date): string {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dd}`;
}

function isoOffset(iso: string, deltaDays: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + deltaDays);
  return localIsoDay(d);
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatRelativeShort(iso: string): string {
  try {
    const ms = Date.now() - Date.parse(iso);
    if (ms < 60_000) return "just now";
    if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch {
    return iso;
  }
}

interface SseHandlers {
  onEvent: (event: string, data: unknown) => void;
}

async function consumeSse(stream: ReadableStream<Uint8Array>, handlers: SseHandlers): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
      }
      if (data) {
        try {
          handlers.onEvent(event, JSON.parse(data));
        } catch {
          handlers.onEvent(event, data);
        }
      }
    }
  }
}
